import { getGmxApiHost } from './registry'

/**
 * Canonical `/apy` window for GMX yields, shared by the vault provider
 * (`fetchGmxVaults`) and the intrinsic-yield fetcher
 * (`yields/intrinsic/fetchers/gmx`) so the two surfaces can't drift to
 * different windows.
 *
 * `1d` on purpose: it is the finest window the API serves and the one we
 * record, so longer windows (7d/30d/90d) can be re-derived from stored
 * history rather than pinned here. Callers that want a specific window can
 * still pass `options.period` to `fetchGmxVaults`.
 */
export const GMX_DEFAULT_APY_PERIOD = '1d'

/** `/apy` entry — fractions (e.g. `0.16` = 16%). */
export interface GmxApyEntry {
  apy: number
  baseApy: number
  bonusApr: number
}

/** `/apy` response: GM markets + GLV vaults, each keyed by token address. */
export interface GmxApyResponse {
  markets: { [address: string]: GmxApyEntry }
  glvs: { [address: string]: GmxApyEntry }
}

/** `/markets` entry. */
export interface GmxMarketMeta {
  name: string
  marketToken: string
  indexToken: string
  longToken: string
  shortToken: string
  isListed: boolean
}

/** `/glvs` entry. */
export interface GmxGlvMeta {
  name: string
  glvToken: string
  longToken: string
  shortToken: string
  isListed: boolean
}

/** `/tokens` entry. */
export interface GmxTokenMeta {
  symbol: string
  address: string
  decimals: number
  synthetic?: boolean
}

const getJson = async <T>(url: string): Promise<T> => {
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`gmxinfra ${url} ${res.status}`)
  return (await res.json()) as T
}

/**
 * Fetch the four gmxinfra endpoints for a chain in parallel: APY (the
 * yield numbers), markets + glvs (metadata: name, long/short/index legs),
 * and tokens (address → symbol map for denomination + leg symbols).
 *
 * Throws if the chain is unsupported or the APY fetch fails; metadata
 * fetches are best-effort (an empty list degrades to no enrichment).
 */
export const fetchGmxRawData = async (
  chainId: string,
  period: string,
  apiUrlOverride?: string,
): Promise<{
  apy: GmxApyResponse
  markets: GmxMarketMeta[]
  glvs: GmxGlvMeta[]
  tokens: GmxTokenMeta[]
}> => {
  const host = apiUrlOverride ?? getGmxApiHost(chainId)
  if (!host) throw new Error(`GMX not supported on chain ${chainId}`)

  const [apy, marketsRes, glvsRes, tokensRes] = await Promise.all([
    getJson<GmxApyResponse>(`${host}/apy?period=${encodeURIComponent(period)}`),
    getJson<{ markets: GmxMarketMeta[] }>(`${host}/markets`).catch(() => ({
      markets: [],
    })),
    getJson<{ glvs: GmxGlvMeta[] }>(`${host}/glvs`).catch(() => ({ glvs: [] })),
    getJson<{ tokens: GmxTokenMeta[] }>(`${host}/tokens`).catch(() => ({
      tokens: [],
    })),
  ])

  return {
    apy,
    markets: marketsRes.markets ?? [],
    glvs: glvsRes.glvs ?? [],
    tokens: tokensRes.tokens ?? [],
  }
}
